import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import {
  DockItemCreate,
  DockItemDelete,
  DockItemInitialState,
  DockItemLoadQr,
  DockItemQrInitialState,
  DockItemSetId,
  DockItemUpdate,
} from './dock-item.actions';
import { DockOverview } from '../models';
import { DockItemState } from './dock-item.state';
import { SetInitialStateFacade } from '../../../../../common/models';
import { HubItemFacade } from '../../hub/item/hub-item.facade';
import { CompartmentItemFacade } from '../../compartment/item/compartment-item.facade';
import { ConfirmationDialogAfterClosedModel } from '../../../../../ui-kit';

@Injectable({ providedIn: 'root' })
export class DockItemFacade implements SetInitialStateFacade {
  dockId$: Observable<number> = this.store.select(DockItemState.getDockId);
  dockQrCode$: Observable<string> = this.store.select(DockItemState.getDockQr);
  isDeleting$: Observable<boolean> = this.store.select(DockItemState.isDeleting);

  constructor(
    private store: Store,
    private hubItemFacade: HubItemFacade,
    private compartmentItemFacade: CompartmentItemFacade,
  ) {}

  setDockId(id: number): void {
    this.store.dispatch(new DockItemSetId(id));
  }

  createDock(dockOverview: DockOverview): void {
    this.getDockRelatedIds().subscribe(([hubId, compartmentId]: [number, number]) =>
      this.store.dispatch(new DockItemCreate({ hubId, compartmentId, dockOverview })),
    );
  }

  updateDock(dockOverview: DockOverview): void {
    this.getDockRelatedIds().subscribe(([hubId, compartmentId]: [number, number]) =>
      this.store.dispatch(
        new DockItemUpdate({
          hubId,
          compartmentId,
          dockOverview,
        }),
      ),
    );
  }

  getDockQr(dockId: number): void {
    this.getDockRelatedIds()
      .pipe(delay(0))
      .subscribe(([hubId, compartmentId]: [number, number]) =>
        this.store.dispatch(
          new DockItemLoadQr({
            hubId,
            compartmentId,
            dockId,
          }),
        ),
      );
  }

  deleteDocks(
    dockIds: number[],
    dialog: MatDialogRef<ConfirmationDialogAfterClosedModel>,
    listRefreshNeeded?: boolean,
  ): void {
    this.getDockRelatedIds().subscribe(([hubId, compartmentId]: [number, number]) =>
      this.store.dispatch(
        new DockItemDelete({
          hubId,
          compartmentId,
          dockIds,
          dialog,
          listRefreshNeeded,
        }),
      ),
    );
  }

  qrSetInitialState(): void {
    this.store.dispatch(new DockItemQrInitialState());
  }

  setInitialState(): void {
    this.store.dispatch(new DockItemInitialState());
  }

  private getDockRelatedIds(): Observable<[number, number]> {
    return combineLatest([this.hubItemFacade.hubId$, this.compartmentItemFacade.compartmentId$]).pipe(take(1));
  }
}
